import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthContext, AuthProvider } from './AuthContext';
import HeaderNav from './components/HeaderNav';
import GlobalNav from './components/GlobalNav';
import Footer from './components/Footer';
import Landing from './components/Landing';
import Web3Pay from './components/Web3Pay';
import Web3Cash from './components/Web3Cash';
import Web3FX from './components/Web3FX';
import Web3Broker from './components/Web3Broker';
import Web3Markets from './components/Web3Markets';
import MoneyTalks from './components/MoneyTalks';
import DBank from './components/DBank';
import Documentation from './components/Documentation';
import ApiReference from './components/ApiReference';
import SupportCommunity from './components/SupportCommunity';
import Features from './components/Features';
import EnsDomain from './components/EnsDomain';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookiePolicy from './components/CookiePolicy';
import SafeGuard from './components/SafeGuard';
import './App.css';
import { mainnet } from 'viem/chains';

const ProtectedRoute = ({ children }) => {
  const context = useContext(AuthContext);
  if (!context) return <p>Loading authentication...</p>;
  const { isAuthenticated, isENSHolder } = context;
  if (!isAuthenticated || !isENSHolder) {
    return (
      <div style={{ color: 'gold', background: 'black', padding: '20px', textAlign: 'center' }}>
        <p>
          Please log in via omnipass.eth and mint your .xbsys.eth to access this feature.
          <a href="https://banksystem.co/identity/" style={{ background: 'gold', color: 'black', padding: '10px', marginLeft: '10px', textDecoration: 'none', borderRadius: '5px' }}>Mint Now</a>
        </p>
      </div>
    );
  }
  return children;
};

const AppContent = () => {
  // Get authentication state from AuthContext - no need for local wallet state anymore
  const { isAuthenticated, address, ensName, loginMethod } = useContext(AuthContext);

  // For backward compatibility, we'll provide walletAddress to components that expect it
  const walletAddress = address;

  return (
    <Router>
      <div>
        {/* HeaderNav now handles all wallet connections internally */}
        <HeaderNav />
        <GlobalNav />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/documentation" element={<Documentation />} />
          <Route path="/api-reference" element={<ApiReference />} />
          <Route path="/support-community" element={<SupportCommunity />} />
          <Route path="/features" element={<Features />} />
          <Route path="/ens-domain" element={<EnsDomain />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />

          {/* Protected routes - require authentication */}
          <Route path="/web3pay" element={<ProtectedRoute><Web3Pay /></ProtectedRoute>} />
          <Route path="/web3cash" element={<ProtectedRoute><Web3Cash /></ProtectedRoute>} />
          <Route path="/web3fx" element={<ProtectedRoute><Web3FX /></ProtectedRoute>} />
          <Route path="/web3broker" element={<ProtectedRoute><Web3Broker /></ProtectedRoute>} />
          <Route path="/web3markets" element={<ProtectedRoute><Web3Markets /></ProtectedRoute>} />
          <Route path="/moneytalks" element={<ProtectedRoute><MoneyTalks /></ProtectedRoute>} />
          <Route path="/dbank" element={<ProtectedRoute><DBank /></ProtectedRoute>} />
          <Route path="/safeguard" element={<ProtectedRoute><SafeGuard /></ProtectedRoute>} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider chain={mainnet}>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
